import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const PropertyTable = ({ properties, onPropertySelect, selectedProperties, onPropertyEdit, onPropertyDelete, onAssignSurvey, clients = [], propertySurveyStatus = {}, className = "" }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const toggleRowExpansion = (propertyId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(propertyId)) {
      newExpanded?.delete(propertyId);
    } else {
      newExpanded?.add(propertyId);
    }
    setExpandedRows(newExpanded);
  };

  // Helper function to get formatted client name with branch
  const getClientDisplay = (property) => {
    const clientName = property?.client || property?.bank_name;

    // Try to find the full client info from the clients list
    if (clients.length > 0 && clientName) {
      const matchingClient = clients.find(client =>
        client.name?.toLowerCase() === clientName?.toLowerCase()
      );

      if (matchingClient && matchingClient.branch) {
        return `${matchingClient.name} - ${matchingClient.branch}`;
      }
    }

    // Fallback to just the client name if no match or no branch
    return clientName || '-';
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onPropertySelect(properties?.map(p => p?.id));
    } else {
      onPropertySelect([]);
    }
  };

  const handleSelectProperty = (propertyId, checked) => {
    if (checked) {
      onPropertySelect([...selectedProperties, propertyId]);
    } else {
      onPropertySelect(selectedProperties?.filter(id => id !== propertyId));
    }
  };

  const isAllSelected = properties?.length > 0 && selectedProperties?.length === properties?.length;
  const isPartiallySelected = selectedProperties?.length > 0 && selectedProperties?.length < properties?.length;

  // Helper function to get survey button text and status
  const getSurveyButtonInfo = (property) => {
    const surveyInfo = propertySurveyStatus[property?.id];
    // Only consider it assigned if surveyInfo exists AND has a survey number
    if (surveyInfo && surveyInfo.surveyNumber) {
      return {
        text: 'Reassign Survey',
        variant: 'outline',
        iconName: 'RefreshCw',
        tooltip: `Survey ${surveyInfo.surveyNumber} assigned to ${surveyInfo.surveyor?.username || 'N/A'} (${surveyInfo.status || 'N/A'})`
      };
    }
    return {
      text: 'Assign Survey',
      variant: 'outline',
      iconName: 'Calendar',
      tooltip: 'Assign a surveyor to this property'
    };
  };

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isPartiallySelected}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left p-4 font-semibold text-foreground w-20">SL.NO</th>
              <th className="text-left p-4 font-semibold text-foreground">
                <button
                  onClick={() => handleSort('property_address')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span>Property Location</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-semibold text-foreground">
                <button
                  onClick={() => handleSort('bank_name')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span>Bank/Client</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-semibold text-foreground">
                <button
                  onClick={() => handleSort('customer_name')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span>Customer Name</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-semibold text-foreground">Contact Person</th>
              <th className="text-left p-4 font-semibold text-foreground">Phone Number</th>
              <th className="w-20 p-4"></th>
            </tr>
          </thead>
          <tbody>
            {properties?.map((property, index) => (
              <React.Fragment key={property?.id}>
                <tr className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="p-4">
                    <Checkbox
                      checked={selectedProperties?.includes(property?.id)}
                      onChange={(e) => handleSelectProperty(property?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="p-4">
                    <span className="text-foreground font-medium">{index + 1}</span>
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-foreground">
                      {property?.property_address || (property?.latitude && property?.longitude
                        ? `${property.latitude}, ${property.longitude}`
                        : property?.latitude || property?.longitude || '-')}
                    </div>
                    {(() => {
                      const surveyInfo = propertySurveyStatus[property?.id];
                      // Only show badge if surveyInfo exists AND has a survey number
                      if (surveyInfo && surveyInfo.surveyNumber) {
                        return (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <Icon name="CheckCircle" size={12} className="mr-1" />
                              Survey Assigned
                            </span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {surveyInfo.surveyNumber}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </td>
                  <td className="p-4">
                    <span className="text-foreground">{getClientDisplay(property)}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-foreground">{property?.customer_name}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-foreground">{property?.contact_person_name}</span>
                  </td>
                  <td className="p-4">
                    <div className="text-foreground">
                      {Array.isArray(property?.contact_person_phone_num)
                        ? property.contact_person_phone_num.map((num, i) => <div key={i}>{num}</div>)
                        : property?.contact_person_phone_num}
                    </div>
                  </td>
                  <td className="p-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleRowExpansion(property?.id)}
                    >
                      <Icon
                        name={expandedRows?.has(property?.id) ? "ChevronUp" : "ChevronDown"}
                        size={16}
                      />
                    </Button>
                  </td>
                </tr>

                {/* Expanded Row Details */}
                {expandedRows?.has(property?.id) && (
                  <tr className="bg-muted/30">
                    <td colSpan="8" className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-foreground mb-3">Full Property Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Bank/Client:</span>
                              <span className="text-foreground">{getClientDisplay(property)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Customer Name:</span>
                              <span className="text-foreground">{property?.customer_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Contact Person:</span>
                              <span className="text-foreground">{property?.contact_person_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Phone Number:</span>
                              <div className="text-foreground text-right">
                                {Array.isArray(property?.contact_person_phone_num)
                                  ? property.contact_person_phone_num.map((num, index) => (
                                    <div key={index}>{num}</div>
                                  ))
                                  : property?.contact_person_phone_num}
                              </div>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Location:</span>
                              <span className="text-foreground text-right max-w-[60%]">
                                {property?.property_address || '-'}
                                {(property?.latitude || property?.longitude) && (
                                  <span className="block text-xs text-muted-foreground">
                                    {property.latitude}, {property.longitude}
                                  </span>
                                )}
                              </span>
                            </div>
                            {(() => {
                              const surveyInfo = propertySurveyStatus[property?.id];
                              // Only show survey details if surveyInfo exists AND has a survey number
                              if (surveyInfo && surveyInfo.surveyNumber) {
                                return (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Survey Number:</span>
                                      <span className="text-foreground font-mono text-xs">{surveyInfo.surveyNumber}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Assigned Surveyor:</span>
                                      <span className="text-foreground">{surveyInfo.surveyor?.username || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Status:</span>
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${surveyInfo.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                          surveyInfo.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                            surveyInfo.status === 'completed' ? 'bg-green-100 text-green-800' :
                                              'bg-gray-100 text-gray-800'
                                        }`}>
                                        {surveyInfo.status || 'N/A'}
                                      </span>
                                    </div>
                                  </>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold text-foreground mb-3">Quick Actions</h4>
                          <div className="flex flex-wrap gap-2">
                            {(() => {
                              const buttonInfo = getSurveyButtonInfo(property);
                              return (
                                <Button
                                  variant={buttonInfo.variant}
                                  size="sm"
                                  iconName={buttonInfo.iconName}
                                  onClick={() => onAssignSurvey && onAssignSurvey(property)}
                                  title={buttonInfo.tooltip}
                                >
                                  {buttonInfo.text}
                                </Button>
                              );
                            })()}
                            <Button variant="outline" size="sm" iconName="FileText">
                              Generate Report
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Edit"
                              onClick={() => onPropertyEdit && onPropertyEdit(property)}
                            >
                              Edit Details
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Trash2"
                              onClick={() => onPropertyDelete && onPropertyDelete(property)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden">
        {properties?.map((property, index) => (
          <div key={property?.id} className="border-b border-border last:border-b-0">
            <div className="p-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={selectedProperties?.includes(property?.id)}
                  onChange={(e) => handleSelectProperty(property?.id, e?.target?.checked)}
                  className="mt-1"
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-muted-foreground">#{index + 1}</span>
                        <h3 className="font-medium text-foreground">
                          {property?.property_address || (property?.latitude && property?.longitude
                            ? `${property.latitude}, ${property.longitude}`
                            : property?.latitude || property?.longitude || '-')}
                        </h3>
                        {(() => {
                          const surveyInfo = propertySurveyStatus[property?.id];
                          // Only show badge if surveyInfo exists AND has a survey number
                          if (surveyInfo && surveyInfo.surveyNumber) {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <Icon name="CheckCircle" size={10} className="mr-1" />
                                Survey
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <p className="text-sm text-muted-foreground">Bank: {getClientDisplay(property)}</p>
                      <p className="text-sm text-muted-foreground">Customer: {property?.customer_name}</p>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleRowExpansion(property?.id)}
                      className="ml-2"
                    >
                      <Icon
                        name={expandedRows?.has(property?.id) ? "ChevronUp" : "ChevronDown"}
                        size={16}
                      />
                    </Button>
                  </div>

                  <div className="mt-2 text-sm">
                    <p className="text-muted-foreground">Contact: {property?.contact_person_name}</p>
                    <div className="text-muted-foreground text-sm">
                      <span>Phone: </span>
                      {Array.isArray(property?.contact_person_phone_num)
                        ? property.contact_person_phone_num.map((num, i) => <div key={i} className="inline-block">{num}</div>)
                        : property?.contact_person_phone_num}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Expanded Details */}
              {expandedRows?.has(property?.id) && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="mb-4">
                    <h4 className="font-medium text-foreground mb-2">Property Details</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bank/Client:</span>
                        <span className="text-foreground">{getClientDisplay(property)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Customer:</span>
                        <span className="text-foreground">{property?.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Contact Person:</span>
                        <span className="text-foreground">{property?.contact_person_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone:</span>
                        <div className="text-foreground text-right">
                          {Array.isArray(property?.contact_person_phone_num)
                            ? property.contact_person_phone_num.map((num, i) => <div key={i}>{num}</div>)
                            : property?.contact_person_phone_num}
                        </div>
                      </div>
                      {(() => {
                        const surveyInfo = propertySurveyStatus[property?.id];
                        // Only show survey details if surveyInfo exists AND has a survey number
                        if (surveyInfo && surveyInfo.surveyNumber) {
                          return (
                            <>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Survey:</span>
                                <span className="text-foreground font-mono text-xs">{surveyInfo.surveyNumber}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Surveyor:</span>
                                <span className="text-foreground">{surveyInfo.surveyor?.username || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Status:</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${surveyInfo.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    surveyInfo.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                      surveyInfo.status === 'completed' ? 'bg-green-100 text-green-800' :
                                        'bg-gray-100 text-gray-800'
                                  }`}>
                                  {surveyInfo.status || 'N/A'}
                                </span>
                              </div>
                            </>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const buttonInfo = getSurveyButtonInfo(property);
                      return (
                        <Button
                          variant={buttonInfo.variant}
                          size="sm"
                          iconName={buttonInfo.iconName}
                          onClick={() => onAssignSurvey && onAssignSurvey(property)}
                          title={buttonInfo.tooltip}
                        >
                          {buttonInfo.text}
                        </Button>
                      );
                    })()}
                    <Button variant="outline" size="sm" iconName="FileText">
                      Report
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onPropertyEdit && onPropertyEdit(property)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onPropertyDelete && onPropertyDelete(property)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PropertyTable;